import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { approach, systemRoles, systems, workTogetherExamples } from '../data/about';
import { easeOutStrong, fadeUp } from '../utils/motion';
const inlineLink = 'text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy transition-colors duration-150';
export function AboutPage() {
  return <>
      {/* Hero */}
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-28" data-id="element-6" data-mp="1fu1wyal40btt">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOutStrong
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-7" data-mp="1avqn3zute1bl">
          <div className="lg:col-span-5" data-id="element-8" data-mp="3rm73wvtg332x">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-9" data-mp="3rn0fca3cmgre">Company</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-7xl text-ink-dark leading-[1.03] tracking-tight" data-id="element-10" data-mp="3mnniqxmodvbm">
              About SyNeural
            </h1>
          </div>
          <div className="lg:col-span-7 lg:pt-10" data-id="element-11" data-mp="3pteuloqmim2n">
            <p className="text-2xl text-ink-dark leading-relaxed mb-6" data-id="element-12" data-mp="3urggehffozht">
              SyNeural builds systems that help people develop sound judgment,
              researchers connect ideas, and organizations turn knowledge into
              action.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-13" data-mp="8blymm0xeekr">
              Our work brings together three complementary parts: the{' '}
              <a href="/semantic-knowledge" className={inlineLink} data-id="element-14" data-mp="hctmsb9q9z14">
                Semantic Knowledge Platform
              </a>
              , which connects and governs knowledge; the{' '}
              <a href="/human-capability" className={inlineLink} data-id="element-15" data-mp="2j46m8ksxfeps">
                Human Capability Platform
              </a>
              , which provides frameworks for assessment and development; and{' '}
              <a href="/noetivra" className={inlineLink} data-id="element-16" data-mp="3rdf8iy0rtx5c">
                Noetivra
              </a>
              , a visual, interactive workspace that makes these resources
              accessible and usable.
            </p>
            <p className="text-lg text-ink-dark leading-relaxed border-l-2 border-teal pl-5" data-id="element-17" data-mp="4a0rmx0y90k2">
              We believe intelligence grows when people can think clearly,
              knowledge can move across disciplines, and institutions can learn
              from their decisions.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Mission */}
      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-18" data-mp="n08o4wa58sra">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-19" data-mp="n1thwjomw7o3">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-20" data-mp="wyohz70k2g4h">
            Our mission
          </h2>
          <div className="lg:col-span-7" data-id="element-21" data-mp="1bkltok7gbzv5">
            <p className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark leading-snug mb-6" data-id="element-22" data-mp="1bln3ymgn8ibt">
              SyNeural’s mission is to strengthen human capability and make
              knowledge more useful in the world.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-23" data-mp="11os70800qpzn">
              We build systems that help people explore ideas, understand
              complex problems, develop their strengths, and make decisions they
              can explain and improve. Our work connects scientific discovery
              with practical applications in education, employment, clinical
              research, civic life, and leadership.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Systems and frameworks */}
      <section className="bg-white py-20 lg:py-28" data-id="element-24" data-mp="9uzxrnhre4q1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25" data-mp="9xcnwnxo7le6">
          <motion.h2 {...fadeUp} className="font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight mb-14" data-id="element-26" data-mp="dvjuipyf76bn">
            Our systems and frameworks
          </motion.h2>
          <div className="border-b border-line" data-id="element-27" data-mp="1vc2al7dpxuc">
            {systems.map(system => <motion.article key={system.name} {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 border-t border-line py-12" data-id="element-28" data-mp="3t7n9de4tp362">
                <div className="lg:col-span-4" data-id="element-29" data-mp="3k9uozvk8v5bt">
                  <h3 className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark mb-3" data-id="element-30" data-mp="3f8x8dnevujcf">
                    {system.name}
                  </h3>
                  {system.tagline && <p className="text-teal font-medium leading-snug" data-id="element-31" data-mp="35bsjvgxkebsg">{system.tagline}</p>}
                </div>
                <div className="lg:col-span-8 space-y-5" data-id="element-32" data-mp="18hpjlerk4m4t">
                  {system.paragraphs.map((paragraph, index) => <p key={paragraph} className={`leading-relaxed ${index === 0 ? 'text-xl text-ink-dark' : 'text-lg text-ink-muted'}`} data-id="element-33" data-mp="13htaj14064ie" data-mpi={index}>
                      {paragraph}
                    </p>)}
                  {system.note && <p className="text-base text-ink-dark leading-relaxed bg-surface-bg border-l-2 border-teal px-5 py-4 rounded-r-md" data-id="element-34" data-mp="1psbhbpmnksv5">
                      {system.note}
                    </p>}
                  <a href={system.link} className="inline-flex items-center gap-2 pt-1 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-35" data-mp="x3491yxecowp">
                    {system.linkLabel}
                    <ArrowRight size={16} aria-hidden="true" data-id="element-36" data-mp="2dhw1gbujlw60" />
                  </a>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* How they work together */}
      <section className="bg-navy-deep text-white py-20 lg:py-28" data-id="element-37" data-mp="1ss1w5q6m2saz">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-38" data-mp="1ntrztc95hlns">
          <motion.h2 {...fadeUp} className="font-heading font-bold text-4xl lg:text-5xl tracking-tight leading-tight mb-12" data-id="element-39" data-mp="1nti9fnk71chk">
            How they work together
          </motion.h2>

          <motion.dl {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/15 border border-white/15 rounded-lg overflow-hidden" data-id="element-40" data-mp="1xq3fmc7y8tmz">
            {systemRoles.map(item => <div key={item.name} className="bg-navy-deep p-6 lg:p-8" data-id="element-41" data-mp="1jrwrvowpsa11">
                <dt className="text-sm text-teal font-medium mb-3" data-id="element-42" data-mp="1jtzpov43z7gb">{item.name}</dt>
                <dd className="font-heading font-bold text-xl leading-snug" data-id="element-43" data-mp="1f059w2rmjcmi">{item.role}</dd>
              </div>)}
          </motion.dl>

          <motion.div {...fadeUp} className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-44" data-mp="mdaq782msha3">
            {workTogetherExamples.map(example => <div key={example.who} className="border-t border-white/20 pt-6" data-id="element-45" data-mp="2xytdis0yfp2z">
                <h3 className="font-heading font-bold text-lg mb-3" data-id="element-46" data-mp="32rmhhdolmte0">{example.who}</h3>
                <p className="text-white/75 leading-relaxed" data-id="element-47" data-mp="2o2zt6mgzuran">That could mean {example.body.charAt(0).toLowerCase() + example.body.slice(1)}</p>
              </div>)}
          </motion.div>

          <motion.p {...fadeUp} className="mt-14 max-w-4xl text-lg text-white leading-relaxed border-l-2 border-green-soft pl-6" data-id="element-48" data-mp="3grnjwxkii8vl">
            Each application would have its own methods, permissions, and
            validation requirements. Sensitive personal records would remain in
            controlled stores, with access appropriate to their purpose.
          </motion.p>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-white py-20 lg:py-28" data-id="element-49" data-mp="20cdxzvs391i2">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-50" data-mp="20d5kmh3pjow7">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-51" data-mp="2n0ceapk9w1wo">
            Our vision
          </h2>
          <div className="lg:col-span-7" data-id="element-52" data-mp="2izirjzikgt5d">
            <p className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark leading-snug mb-6" data-id="element-53" data-mp="2e0pda4vggy45">
              SyNeural’s long-term vision is a global infrastructure for
              connected knowledge and human capability.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed mb-5" data-id="element-54" data-mp="36pmus5rueqvu">
              We see a future where researchers can discover connections across
              fields, people have clearer pathways to develop their strengths,
              and institutions can examine the reasoning behind their decisions.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed mb-5" data-id="element-55" data-mp="3pnpdy3xpltlq">
              That vision extends from individual learning to collaboration
              across organizations and public systems. It requires both useful
              technology and accountable institutions: people who approve
              methods, review outcomes, correct mistakes, and take
              responsibility for consequential decisions.
            </p>
            <p className="text-lg text-ink-dark leading-relaxed" data-id="element-56" data-mp="eua8azfm2gfg">
              The goal is to expand what people are capable of understanding and
              accomplishing together.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Leadership */}
      <section className="bg-surface-bg py-20 lg:py-28 border-y border-line" data-id="element-57" data-mp="thlk92fpg2g3">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-58" data-mp="yh89694wb74q">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-59" data-mp="12kedhb8g6o8k">
            Leadership
          </h2>
          <div className="lg:col-span-7 flex flex-col sm:flex-row gap-8" data-id="element-60" data-mp="2a6y5mgrec886">
            <img src="https://cdn.magicpatterns.com/uploads/aGzifJuZ6XFUAFRAawshw8/IMG_1407.jpg" alt="Portrait of Shawn Blake, Lead Developer and Founder of SyNeural" width={160} height={200} loading="lazy" className="w-32 h-40 sm:w-40 sm:h-48 shrink-0 rounded-lg object-cover object-[50%_30%] border border-line bg-surface-muted" data-id="element-61" data-mp="2a4va5h7h5mw2" />
            <div data-id="element-62" data-mp="2xvr70sqsf8a6">
              <h3 className="font-heading font-bold text-2xl text-ink-dark" data-id="element-63" data-mp="2xv94xfhg1zuc">Shawn Blake</h3>
              <p className="text-teal font-medium mb-5" data-id="element-64" data-mp="32v3rj6u6s39l">Lead Developer &amp; Founder</p>
              <p className="text-lg text-ink-muted leading-relaxed" data-id="element-65" data-mp="37uzy9du9q8su">
                Shawn Blake leads SyNeural’s work across cognitive systems,
                semantic knowledge, and institutional design. His focus is
                bringing these disciplines together to help researchers connect
                ideas, people develop their capabilities, and organizations turn
                complex knowledge into responsible action.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Approach */}
      <section className="bg-white py-20 lg:py-28" data-id="element-66" data-mp="3hyn32u1rae8p">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-67" data-mp="3d03fkp7ikue8">
          <motion.h2 {...fadeUp} className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-68" data-mp="dj5emd24a5d5">
            The SyNeural approach
          </motion.h2>
          <motion.dl {...fadeUp} className="lg:col-span-7 border-t border-line" data-id="element-69" data-mp="4hntgg9urdhl">
            {approach.map(item => <div key={item.title} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-8 py-6 border-b border-line" data-id="element-70" data-mp="3vs5yk77momrn">
                <dt className="sm:col-span-4 font-heading font-bold text-lg text-ink-dark" data-id="element-71" data-mp="2k91z8ks34st4">{item.title}</dt>
                <dd className="sm:col-span-8 text-ink-muted leading-relaxed" data-id="element-72" data-mp="2lslcghhtghnk">{item.body}</dd>
              </div>)}
          </motion.dl>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white py-20 lg:py-28" data-id="element-73" data-mp="2cqubfpy80z01">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-74" data-mp="2hpfb9o9k9gny">
          <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight mb-6" data-id="element-75" data-mp="2312bshb9lfop">
            Build with SyNeural
          </h2>
          <p className="text-xl text-white/80 leading-relaxed mb-10" data-id="element-76" data-mp="27tbyloh31zmr">
            Whether you are connecting scientific research, developing human
            capability, or helping organizations collaborate and make better
            decisions, SyNeural is building the systems and frameworks to
            support that work.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-77" data-mp="1p19q50elhpnl">
            <a href="/#what-we-build" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-78" data-mp="1p3dqj8dac6j4">
              Explore our systems
              <ArrowRight size={18} aria-hidden="true" data-id="element-79" data-mp="1fbkpaoev5cya" />
            </a>
            <a href="/partnerships" className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-80" data-mp="1ad9gmh0b18hn">
              Start a partnership conversation
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}